import type { ReactNode } from "react";

import { cn } from "../cn.js";
import { SkeletonRows } from "./Feedback.js";

export interface Column<T> {
	id: string;
	header: string;
	render: (row: T) => ReactNode;
	/** Hidden on narrow screens where the stacked card layout takes over. */
	className?: string;
	align?: "left" | "right";
	/** Shown as the card title in the mobile layout. */
	primary?: boolean;
}

export interface DataTableProps<T> {
	columns: Column<T>[];
	rows: T[];
	rowKey: (row: T) => string;
	loading?: boolean;
	empty?: ReactNode;
	onRowClick?: (row: T) => void;
	caption?: string;
	className?: string;
}

/**
 * Responsive list.
 *
 * Renders a table on wide screens and stacked cards below `sm`, rather than
 * compressing a table into 390 px (spec §3.5, §20). Both layouts read from the
 * same column definitions, so they cannot drift apart.
 */
export function DataTable<T>({
	columns,
	rows,
	rowKey,
	loading = false,
	empty,
	onRowClick,
	caption,
	className,
}: DataTableProps<T>) {
	if (loading) return <SkeletonRows rows={5} className={className} />;
	if (rows.length === 0 && empty) return <>{empty}</>;

	const primary = columns.find((column) => column.primary) ?? columns[0];
	const secondary = columns.filter((column) => column !== primary);

	return (
		<div className={className}>
			{/* Desktop */}
			<table className="hidden w-full border-separate border-spacing-0 text-sm sm:table">
				{caption && <caption className="sr-only">{caption}</caption>}
				<thead>
					<tr className="border-b border-(--border-subtle)">
						{columns.map((column) => (
							<th
								key={column.id}
								scope="col"
								className={cn(
									"px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-(--text-muted)",
									column.align === "right" ? "text-right" : "text-left",
									column.className,
								)}
							>
								{column.header}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{rows.map((row) => (
						<tr
							key={rowKey(row)}
							onClick={onRowClick ? () => onRowClick(row) : undefined}
							className={cn(
								"border-b border-(--border-subtle) last:border-0",
								"transition-colors duration-150 hover:bg-(--surface-sunken)",
								onRowClick && "cursor-pointer",
							)}
						>
							{columns.map((column) => (
								<td
									key={column.id}
									className={cn(
										"px-4 py-3 align-middle text-(--text-primary)",
										column.align === "right" ? "text-right" : "text-left",
										column.className,
									)}
								>
									{column.render(row)}
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>

			{/* Mobile */}
			<ul className="flex flex-col sm:hidden">
				{rows.map((row) => (
					<li key={rowKey(row)} className="border-b border-(--border-subtle) last:border-0">
						<div
							role={onRowClick ? "button" : undefined}
							tabIndex={onRowClick ? 0 : undefined}
							onClick={onRowClick ? () => onRowClick(row) : undefined}
							onKeyDown={
								onRowClick
									? (event) => {
											if (event.key === "Enter" || event.key === " ") {
												event.preventDefault();
												onRowClick(row);
											}
										}
									: undefined
							}
							className={cn("flex flex-col gap-2 px-4 py-3.5", onRowClick && "active:bg-(--surface-sunken)")}
						>
							{primary && <div className="text-sm font-medium">{primary.render(row)}</div>}
							<dl className="flex flex-col gap-1">
								{secondary.map((column) => (
									<div key={column.id} className="flex items-baseline justify-between gap-3">
										<dt className="text-xs text-(--text-muted)">{column.header}</dt>
										<dd className="text-sm text-(--text-secondary)">{column.render(row)}</dd>
									</div>
								))}
							</dl>
						</div>
					</li>
				))}
			</ul>
		</div>
	);
}
